"use client";

import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CircleHelp } from "lucide-react";
import { z } from "zod/v3";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { OptionalEmailFields } from "~/components/ui/optional-email-fields";
import { QuestionChoice } from "~/components/ui/question-choice";
import { QuestionFlowShell } from "~/components/ui/question-flow-shell";
import { QuestionTextField } from "~/components/ui/question-text-field";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { cn } from "~/lib/utils";

import {
  type TherapistMatchingAnswer,
  type TherapistMatchingQuestion,
} from "./questions";

type GuidedQuestionnaireProps = {
  questions: TherapistMatchingQuestion[];
  therapyType: "INDIVIDUAL" | "COUPLES";
  initialEmail?: string | null;
  errorMessage?: string | null;
  isSubmitting?: boolean;
  onBackToStart?: () => void;
  onSubmit: (data: {
    answers: Array<{ question: string; answer: TherapistMatchingAnswer }>;
    email?: string;
    newsletterOptIn: boolean;
  }) => void;
};

const isAnswered = (answer: TherapistMatchingAnswer | undefined) =>
  Array.isArray(answer) ? answer.length > 0 : Boolean(answer?.trim());

const emailSchema = z.string().email("Unesi ispravnu email adresu.");

export function GuidedQuestionnaire({
  questions,
  therapyType,
  initialEmail,
  errorMessage,
  isSubmitting = false,
  onBackToStart,
  onSubmit,
}: GuidedQuestionnaireProps) {
  const [answers, setAnswers] = useState<
    Record<string, TherapistMatchingAnswer>
  >({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isOnEmailStep, setIsOnEmailStep] = useState(false);
  const [email, setEmail] = useState(initialEmail ?? "");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  const visibleQuestions = useMemo(
    () =>
      questions.filter(
        (question) => !question.showWhen || question.showWhen(answers),
      ),
    [answers, questions],
  );
  const currentQuestion = isOnEmailStep
    ? undefined
    : visibleQuestions[questionIndex];
  const currentAnswer = currentQuestion
    ? answers[currentQuestion.id]
    : undefined;
  useEffect(() => {
    if (questionIndex >= visibleQuestions.length) {
      setQuestionIndex(Math.max(visibleQuestions.length - 1, 0));
    }
  }, [questionIndex, visibleQuestions.length]);

  useEffect(() => {
    if (!currentQuestion) return;

    capturePostHogEvent("questionnaire_step_viewed", {
      therapy_type: therapyType,
      question_id: currentQuestion.id,
      question_position: questionIndex + 1,
      section: currentQuestion.section,
    });
  }, [currentQuestion, questionIndex, therapyType]);

  const updateAnswer = (value: TherapistMatchingAnswer) => {
    if (!currentQuestion) return;
    setAnswers((previous) => ({ ...previous, [currentQuestion.id]: value }));
  };

  const handleNext = () => {
    if (isOnEmailStep) {
      const trimmedEmail = email.trim();
      if (trimmedEmail.length > 0) {
        const parsedEmail = emailSchema.safeParse(trimmedEmail);
        if (!parsedEmail.success) {
          setEmailError(parsedEmail.error.issues[0]?.message ?? null);
          return;
        }
      } else if (newsletterOptIn) {
        setEmailError("Unesi email da bismo ti slali savete i ponude.");
        return;
      }

      onSubmit({
        answers: visibleQuestions.flatMap((question) => {
          const answer = answers[question.id];
          return answer && isAnswered(answer)
            ? [{ question: question.question, answer }]
            : [];
        }),
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        newsletterOptIn,
      });
      return;
    }

    if (!currentQuestion) return;
    if (!currentQuestion.optional && !isAnswered(currentAnswer)) return;

    capturePostHogEvent("questionnaire_step_completed", {
      therapy_type: therapyType,
      question_id: currentQuestion.id,
      question_position: questionIndex + 1,
      section: currentQuestion.section,
    });

    if (questionIndex < visibleQuestions.length - 1) {
      setQuestionIndex((value) => value + 1);
      return;
    }

    setIsOnEmailStep(true);
  };

  const questionNumber = isOnEmailStep
    ? visibleQuestions.length + 1
    : questionIndex + 1;
  const totalSteps = visibleQuestions.length + 1;
  const therapyTypeLabel =
    therapyType === "COUPLES" ? "Partnerska terapija" : "Individualna terapija";
  const hasAnswer = isOnEmailStep || isAnswered(currentAnswer);
  const isCheckboxQuestion = currentQuestion?.type === "checkbox";
  const questionHeadingId = isOnEmailStep
    ? "matching-email-heading"
    : currentQuestion
      ? `matching-question-${currentQuestion.id}`
      : undefined;
  const questionHelperId =
    !isOnEmailStep && currentQuestion?.helperText
      ? `matching-question-${currentQuestion.id}-helper`
      : undefined;

  return (
    <section className="min-h-[calc(100dvh-4rem)] bg-background lg:min-h-0">
      <QuestionFlowShell
        maxWidthClassName="max-w-4xl"
        context={therapyTypeLabel}
        headerAction={
          !isOnEmailStep && questionIndex === 0 && onBackToStart ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBackToStart}
              className="shrink-0 text-brand hover:text-brand/80"
            >
              Promeni vrstu terapije
            </Button>
          ) : undefined
        }
        progressLabel={
          <>
            Korak {questionNumber} od {totalSteps}
            {isOnEmailStep ? ", email (opciono)" : ""}
          </>
        }
        progressAriaLabel={`Napredak, korak ${questionNumber} od ${totalSteps}${
          isOnEmailStep ? ", email (opciono)" : ""
        }`}
        progressMax={totalSteps}
        progressValue={questionNumber}
      >
        <div className="pt-8 sm:pt-10">
          {isOnEmailStep ? (
            <>
              <h1
                id={questionHeadingId}
                className="max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
              >
                Sačuvaj preporuke
              </h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-muted-foreground">
                Unesi email ako želiš da ti pošaljemo link. Možeš i da nastaviš
                bez emaila.
              </p>
            </>
          ) : (
            <h1
              id={questionHeadingId}
              className="max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            >
              {currentQuestion!.question}
            </h1>
          )}
          {!isOnEmailStep && currentQuestion?.helperText && (
            <p
              id={questionHelperId}
              className="mt-4 max-w-xl text-base leading-7 text-muted-foreground"
            >
              {currentQuestion.helperText}
            </p>
          )}

          <div className={cn("mt-8", !isCheckboxQuestion && "max-w-2xl")}>
            {isOnEmailStep && (
              <OptionalEmailFields
                id="matching-email"
                email={email}
                emailError={emailError}
                newsletterOptIn={newsletterOptIn}
                onEmailChange={(value) => {
                  setEmail(value);
                  setEmailError(null);
                }}
                onNewsletterOptInChange={setNewsletterOptIn}
              />
            )}
            {!isOnEmailStep && currentQuestion?.type === "radio" && (
              <RadioGroup
                value={typeof currentAnswer === "string" ? currentAnswer : ""}
                onValueChange={updateAnswer}
                className="grid gap-3"
                aria-labelledby={questionHeadingId}
                aria-describedby={questionHelperId}
              >
                {currentQuestion.options?.map((option) => {
                  const selected = currentAnswer === option;
                  return (
                    <QuestionChoice
                      key={option}
                      selected={selected}
                      control={
                        <RadioGroupItem
                          value={option}
                          className="shrink-0 border-input text-brand data-[state=checked]:border-brand"
                        />
                      }
                    >
                      {option}
                    </QuestionChoice>
                  );
                })}
              </RadioGroup>
            )}

            {!isOnEmailStep && currentQuestion?.type === "checkbox" && (
              <div
                className="grid gap-3 sm:grid-cols-2"
                role="group"
                aria-labelledby={questionHeadingId}
                aria-describedby={questionHelperId}
              >
                {currentQuestion.options?.map((option) => {
                  const selected =
                    Array.isArray(currentAnswer) &&
                    currentAnswer.includes(option);
                  return (
                    <QuestionChoice
                      key={option}
                      selected={selected}
                      control={
                        <Checkbox
                          checked={selected}
                          onCheckedChange={(checked) => {
                            const selectedAnswers = Array.isArray(currentAnswer)
                              ? currentAnswer
                              : [];
                            updateAnswer(
                              checked
                                ? [...selectedAnswers, option]
                                : selectedAnswers.filter(
                                    (item) => item !== option,
                                  ),
                            );
                          }}
                          className="shrink-0 border-input data-[state=checked]:border-brand data-[state=checked]:bg-brand-action data-[state=checked]:text-white"
                        />
                      }
                    >
                      {option}
                    </QuestionChoice>
                  );
                })}
              </div>
            )}

            {!isOnEmailStep && currentQuestion?.type === "textarea" && (
              <QuestionTextField
                value={typeof currentAnswer === "string" ? currentAnswer : ""}
                onChange={updateAnswer}
                placeholder={currentQuestion.placeholder}
                ariaLabelledBy={questionHeadingId}
                ariaDescribedBy={questionHelperId}
                multiline
              />
            )}

            {!isOnEmailStep && currentQuestion?.type === "text" && (
              <QuestionTextField
                value={typeof currentAnswer === "string" ? currentAnswer : ""}
                onChange={updateAnswer}
                placeholder={currentQuestion.placeholder}
                ariaLabelledBy={questionHeadingId}
                ariaDescribedBy={questionHelperId}
              />
            )}
          </div>

          <div className="mt-10 flex items-center justify-end gap-3 border-t border-border pt-5">
            {(isOnEmailStep || questionIndex > 0) && (
              <Button
                type="button"
                variant="ghost"
                size="lg"
                onClick={() =>
                  isOnEmailStep
                    ? setIsOnEmailStep(false)
                    : setQuestionIndex((value) => value - 1)
                }
                disabled={isSubmitting}
                className="mr-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Nazad
              </Button>
            )}
            <Button
              type="button"
              variant="cta"
              size="lg"
              onClick={handleNext}
              disabled={
                isSubmitting ||
                (!isOnEmailStep && !currentQuestion?.optional && !hasAnswer)
              }
              isLoading={isSubmitting}
            >
              {isSubmitting
                ? "Otvaramo preporuke"
                : isOnEmailStep
                  ? email.trim().length > 0
                    ? "Pošalji link i prikaži preporuke"
                    : "Prikaži preporuke"
                  : "Nastavi"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {!isOnEmailStep && currentQuestion?.optional && !hasAnswer && (
            <p className="mt-3 flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
              <CircleHelp className="h-3.5 w-3.5" /> Možeš nastaviti i bez
              odgovora
            </p>
          )}
          {errorMessage && (
            <p
              className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm leading-6 text-foreground"
              role="alert"
            >
              {errorMessage}
            </p>
          )}
        </div>
      </QuestionFlowShell>
    </section>
  );
}
